import {
  Webhook as WebhookDto,
  WebhookCreationRequest as WebhookCreationRequestDto,
  WebhookDeliveryStatusEnum,
  WebhookPreview as WebhookPreviewDto,
  WebhookProviderEnum,
  WebhookProvidersResponse as WebhookProvidersResponseDto,
  WebhookStatusEnum,
  WebhookTestSendResult as WebhookTestSendResultDto,
  WebhookUpdateRequest as WebhookUpdateRequestDto,
} from '@electricjs/core_entity-client';
import { http, HttpResponse, delay } from 'msw';

import {
  WEBHOOK_LIMIT,
  buildMockWebhookDeliveries,
  buildMockWebhooks,
  mockWebhookProviders,
} from '@/mocks/data/webhookData';

// In-memory store so create/edit/delete survive refetches until page reload.
let webhooks = buildMockWebhooks();

export const webhookHandlers = [
  http.get('*/v1/organizations/:organizationId/webhooks', async () => {
    await delay();
    return HttpResponse.json(
      { results: webhooks, total: webhooks.length, limit: WEBHOOK_LIMIT },
      { status: 200 }
    );
  }),

  http.post(
    '*/v1/organizations/:organizationId/webhooks',
    async ({ params, request }) => {
      const body = (await request.json()) as WebhookCreationRequestDto;
      const webhook: WebhookDto = {
        id: `webhook-${crypto.randomUUID()}`,
        organization_id: String(params.organizationId),
        name: body.name,
        url: body.url,
        topics: body.topics,
        status: body.status ?? WebhookStatusEnum.Active,
        provider: body.provider ?? WebhookProviderEnum.Generic,
        config: body.config ?? {},
        has_credentials: Object.keys(body.credentials ?? {}).length > 0,
        created_at: new Date().toISOString(),
      };
      webhooks = [...webhooks, webhook];

      await delay();
      // The signing secret is only ever returned on creation.
      return HttpResponse.json(
        { ...webhook, secret: 'whsec_mock_signing_secret' },
        { status: 201 }
      );
    }
  ),

  // Static routes below must be registered BEFORE the `/webhooks/:webhookId` ones.
  http.get(
    '*/v1/organizations/:organizationId/webhooks/providers',
    async () => {
      const response: WebhookProvidersResponseDto = {
        providers: mockWebhookProviders,
      };
      await delay();
      return HttpResponse.json(response, { status: 200 });
    }
  ),

  http.get(
    '*/v1/organizations/:organizationId/webhooks/preview',
    async ({ request }) => {
      const url = new URL(request.url);
      const topic = url.searchParams.get('topic');
      const eventType = url.searchParams.get('event_type');
      const response: WebhookPreviewDto = {
        content_type: 'application/json',
        headers: { 'X-Electric-Signature': '********' },
        body: JSON.stringify(
          { topic, event_type: eventType, data: { id: 'mock-id' } },
          null,
          2
        ),
      };
      await delay();
      return HttpResponse.json(response, { status: 200 });
    }
  ),

  http.post(
    '*/v1/organizations/:organizationId/webhooks/test',
    async ({ request }) => {
      const body = (await request.json()) as {
        topic: string;
        event_type: string;
      };
      const response: WebhookTestSendResultDto = {
        topic: body.topic,
        event_type: body.event_type,
        status: WebhookDeliveryStatusEnum.Success,
        response_status: 200,
        latency_ms: 120,
      };
      await delay();
      return HttpResponse.json(response, { status: 200 });
    }
  ),

  http.patch(
    '*/v1/organizations/:organizationId/webhooks/:webhookId',
    async ({ params, request }) => {
      const existing = webhooks.find(w => w.id === params.webhookId);
      await delay();
      if (!existing) {
        return new HttpResponse(null, { status: 404 });
      }

      // `credentials` is write-only: it only flips `has_credentials`.
      const { credentials = {}, ...changes } =
        (await request.json()) as WebhookUpdateRequestDto;
      const updated: WebhookDto = {
        ...existing,
        ...changes,
        has_credentials:
          existing.has_credentials || Object.keys(credentials).length > 0,
        updated_at: new Date().toISOString(),
      };
      webhooks = webhooks.map(w => (w.id === existing.id ? updated : w));
      return HttpResponse.json(updated, { status: 200 });
    }
  ),

  http.delete(
    '*/v1/organizations/:organizationId/webhooks/:webhookId',
    async ({ params }) => {
      const exists = webhooks.some(w => w.id === params.webhookId);
      webhooks = webhooks.filter(w => w.id !== params.webhookId);

      await delay();
      return new HttpResponse(null, { status: exists ? 204 : 404 });
    }
  ),

  http.post(
    '*/v1/organizations/:organizationId/webhooks/:webhookId/test',
    async ({ params, request }) => {
      const body = (await request.json()) as {
        topic: string;
        event_type: string;
      };
      await delay();
      return HttpResponse.json(
        {
          id: `${params.webhookId}-test-delivery`,
          webhook_id: params.webhookId,
          event_type: body.event_type,
          topic: body.topic,
          is_test: true,
          status: WebhookDeliveryStatusEnum.Success,
          response_status: 200,
          latency_ms: 120,
          created_at: new Date().toISOString(),
        },
        { status: 200 }
      );
    }
  ),

  http.get(
    '*/v1/organizations/:organizationId/webhooks/:webhookId/deliveries',
    async ({ params }) => {
      await delay();
      return HttpResponse.json(
        buildMockWebhookDeliveries(String(params.webhookId)),
        { status: 200 }
      );
    }
  ),
];
